import React, { useState, useEffect, useRef } from "react";
import profileImg from "../../../../../assets/images/user-default.svg";
import YhInput from "../../../../../ui/YhInput";
import YhSelect from "../../../../../ui/YhSelect";
import YhButton from "../../../../../ui/YhButton";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../helpers/methods";
import notifiers from "../../../../../global/constants/NotificationConstants";
import customErrorHandler from "../../../../../utils/customErrorHandler";
import CustomLoader from "../../../../../global/components/CustomLoader/CustomLoader";
import {
  getFinalBodyStructure,
  handleValidation,
  initialState,
  validateInput,
} from "./Validation";
import { getCountry, getState } from "./PersonalDetailsService";
import {
  RegisterEmployee,
  getPerosnalDetails,
  updatePersonalDetail,
} from "../StepperService";
import { ImageUpLoad } from "../EmployeeDocuments/EmployeeDocumentServices";
import OrganizationSelect from "../../../../../Shared/Organization/OrganizationSelect";
import {
  listUserOrganizations,
  syncUserOrganizations,
} from "../../../../../services/organizationService";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../../../../redux/authSlice";

const _ = require("lodash");

const options = ["Single", "Married"];
const ganderOptions = ["Male", "Female"];
const countryData = [
  {
    label: "India",
    code: "IN",
    dialCode: "+91",
    flag: "https://example.com/india_flag.jpg",
    capital: "New Delhi",
    class: <span className="fi fi-in"></span>,
  },
  {
    label: "USA",
    code: "US",
    dialCode: "+1",
    flag: "https://example.com/usa_flag.jpg",
    capital: "Washington, D.C.",
    class: <span className="fi fi-us"></span>,
  },
  {
    label: "UAE",
    code: "AE",
    dialCode: "+971",
    flag: "https://example.com/uae_flag.jpg",
    capital: "Abu Dhabi",
    class: <span className="fi fi-ae"></span>,
  },
];

let clonedObject = {};

interface CustomProps {
  nextStep?: () => void | undefined;
  userId: String;
  setUserId?: Function;
  fromSettingOrLogout?: any;
  registerStepActions?: (actions: { submit: () => void | Promise<void> }) => void;
  hideFooterButtons?: boolean;
  onProfileImageChange?: (url: string) => void;
}

const PersonalDetails = (props: CustomProps & { onProfileImageChange?: (url: string) => void }) => {
  const [personalDetailState, setPersonalDetailState] = useState<any>(
    initialState({})
  );
  const [apiResponse, setApiResponse] = useState(false);
  const [country, setCountry] = useState<any>([]);
  const [state, setState] = useState<any>([]);
  const [avatarImg, setAvatarImg] = useState(profileImg);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const [orgIdsLoaded, setOrgIdsLoaded] = useState(false);
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const showOrgSelect = props.fromSettingOrLogout !== "fromSettingOrLogout";

  useEffect(() => {
    init();
    loadCountries();
  }, [props.userId]);

  useEffect(() => {
    if (!showOrgSelect) return;
    if (orgIdsLoaded) return;
    if (organizationIds.length) return;
    const active =
      activeOrganizationId && activeOrganizationId !== "all"
        ? String(activeOrganizationId)
        : "";
    if (active) setOrganizationIds([active]);
  }, [showOrgSelect, orgIdsLoaded, organizationIds.length, activeOrganizationId]);

  const handleOnChangeInputField = (event: any) => {
    const name: any = event.target.name;
    const value: any = event.target.value;
    const isGoForward: any = validateInput(name, value);
    if (!isGoForward) return;
    setPersonalDetailState((prevFormData: any) => ({
      ...prevFormData,
      [name]: { error: "", value },
    }));
  };

  const handleAutocompleteChange = (name: string, value: any) => {
    const isGoForward: any = validateInput(name, value);
    if (!isGoForward) return;
    if (name === "country_id" || name === "state_id") {
      setPersonalDetailState((prevFormData: any) => ({
        ...prevFormData,
        [name]: {
          error: "",
          value: value.country_name ?? value.state_name,
          id: value._id,
        },
      }));
    } else if (
      name === "martial_status" ||
      name === "gender" ||
      name === "state_id"
    ) {
      setPersonalDetailState((prevFormData: any) => ({
        ...prevFormData,
        [name]: { error: "", value: value },
      }));
    } else if (name === "whatsappCountryCode" || name === "phoneCountryCode") {
      const safe = {
        label: value?.label || "",
        code: value?.code || "",
        dialCode: value?.dialCode || "",
      };
      setPersonalDetailState((prevFormData: any) => ({
        ...prevFormData,
        [name]: { error: "", value: safe },
      }));
    }
  };

  const init = async () => {
    if (props.userId) {
      setApiResponse(true);
      try {
        let response = await getPerosnalDetails(props.userId);
        customErrorHandler(response);
        const nextState = initialState(response.data, true);
        setPersonalDetailState(nextState);
        clonedObject = _.cloneDeep(nextState);
        // Show the already-saved profile photo
        const savedImg = response.data?.profile_image || response.data?.[0]?.profile_image;
        if (savedImg) {
          setAvatarImg(savedImg);
          props.onProfileImageChange?.(savedImg);
        }
        if (showOrgSelect) {
          try {
            const orgRes = await listUserOrganizations(String(props.userId));
            const ids = Array.isArray(orgRes?.organizationIds)
              ? orgRes.organizationIds.map(String)
              : Array.isArray(orgRes?.data)
                ? orgRes.data.map((m: any) => String(m.organizationId))
                : [];
            setOrganizationIds(ids.filter(Boolean));
            setOrgIdsLoaded(true);
          } catch {
            setOrgIdsLoaded(true);
          }
        }
        if (nextState?.country_id?.id) {
          try {
            const stateRes = await getState(nextState.country_id.id);
            customErrorHandler(stateRes);
            setState(
              stateRes.data.map((department: any) => ({
                _id: department._id,
                state_name: department.state_name,
              }))
            );
          } catch {
            // ignore state load failure on init
          }
        }
        setApiResponse(false);
      } catch (error: any) {
        setApiResponse(false);
        openErrorNotification(
          isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
        );
      }
    }
  };

  const persistOrganizations = async (userId: string) => {
    if (!showOrgSelect || !userId) return;
    try {
      await syncUserOrganizations(userId, organizationIds);
    } catch (err: any) {
      openErrorNotification(
        isTruthy(err?.message)
          ? err.message
          : "Saved profile, but organization membership failed"
      );
    }
  };

  const loadCountries = async () => {
    try {
      const response = await getCountry();
      customErrorHandler(response);
      setCountry(
        response.data.map((department: any) => ({
          _id: department._id,
          country_name: department.country_name,
        }))
      );
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const onClickAutoComplet = async (type: any) => {
    if (type === "country") {
      await loadCountries();
    }
    if (type === "state") {
      try {
        const response = await getState(personalDetailState.country_id.id);
        customErrorHandler(response);
        let updatedOptions = response.data.map((department: any) => ({
          _id: department._id,
          state_name: department.state_name,
        }));
        setState(updatedOptions);
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error?.message) ? error?.message : notifiers.GENERIC_ERROR
        );
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const data = new FormData();
    data.append("file", file);

    const reader = new FileReader();
    reader.onload = (e: any) => setAvatarImg(e.target.result);
    reader.readAsDataURL(file);

    try {
      const uploadedUrl = await ImageUpLoad(data);
      if (uploadedUrl?.imageUrl) {
        setPersonalDetailState((prevFormData: any) => ({
          ...prevFormData,
          profile_image: { error: "", value: uploadedUrl.imageUrl },
        }));
        props.onProfileImageChange?.(uploadedUrl.imageUrl);
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      // Reset so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submitForm = async () => {
    const validatedObject: any = handleValidation(personalDetailState);
    if (
      validatedObject?.name.error ||
      validatedObject.phone.error ||
      validatedObject.email.error ||
      validatedObject.whatsapp.error ||
      validatedObject.dob.error ||
      validatedObject.martial_status.error ||
      validatedObject.gender.error ||
      validatedObject.country_id.error ||
      validatedObject.state_id.error ||
      validatedObject.city.error ||
      validatedObject.zipcode.error ||
      validatedObject.employee_id.error ||
      validatedObject.address1.error
    ) {
      setPersonalDetailState({ ...validatedObject });
      return;
    }
    if (props.userId && _.isEqual(clonedObject, validatedObject)) {
      await persistOrganizations(String(props.userId));
      props.nextStep?.();
    } else if (props.userId) {
      try {
        const body = {
          ...personalDetailState,
          whatsappCountryCode: {
            value: serializeCountryCode(
              personalDetailState?.whatsappCountryCode
            ),
          },
          phoneCountryCode: {
            value: serializeCountryCode(personalDetailState?.phoneCountryCode),
          },
        };
        body.userId = { value: props.userId, error: "" };
        const response = await updatePersonalDetail(
          getFinalBodyStructure(body)
        );
        if (response?.success) {
          await persistOrganizations(String(props.userId));
          openSuccessNotification(response?.message);
          // Propagate saved profile image to parent hero after save
          const savedImg = personalDetailState?.profile_image?.value;
          if (savedImg) props.onProfileImageChange?.(savedImg);
          props.nextStep?.();
        } else {
          customErrorHandler(response);
        }
      } catch (error: any) {
        // keep silent like original
      }
    } else {
      try {
        const body = {
          ...personalDetailState,
          whatsappCountryCode: {
            value: serializeCountryCode(
              personalDetailState?.whatsappCountryCode
            ),
          },
          phoneCountryCode: {
            value: serializeCountryCode(personalDetailState?.phoneCountryCode),
          },
        };
        const response = await RegisterEmployee(getFinalBodyStructure(body));
        if (response?.success) {
          const newId = response.data._id;
          props?.setUserId && props?.setUserId(newId);
          setPersonalDetailState(initialState(response.data, true));
          await persistOrganizations(String(newId));
          openSuccessNotification(response?.message);
          props.nextStep?.();
        } else {
          customErrorHandler(response);
        }
      } catch (error: any) {
        // keep silent like original
      }
    }
  };

  useEffect(() => {
    props.registerStepActions?.({ submit: submitForm });
  });

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const inputs = document.getElementsByTagName("input");
      const currentIndex = Array.from(inputs).indexOf(
        event.target as HTMLInputElement
      );
      const nextIndex = currentIndex + 1;

      if (nextIndex < inputs.length) {
        inputs[nextIndex].focus();
      } else {
        submitForm();
      }
    }
  };

  const serializeCountryCode = (raw: any) => {
    const v = raw?.value ?? raw;
    if (!v || typeof v !== "object") {
      return JSON.stringify(v || {});
    }
    return JSON.stringify({
      label: v.label || "",
      code: v.code || "",
      dialCode: v.dialCode || "",
    });
  };
  const phoneCode = personalDetailState?.phoneCountryCode?.value?.code || "";
  const whatsappCode =
    personalDetailState?.whatsappCountryCode?.value?.code || "";
  const countryId = personalDetailState?.country_id?.id || "";
  const stateId = personalDetailState?.state_id?.id || "";

  // dd-mm-yyyy display -> yyyy-mm-dd for date input
  const dobForInput = (() => {
    const raw = personalDetailState?.dob?.value;
    if (!raw || typeof raw !== "string") return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    const parts = raw.split(/[-/]/);
    if (parts.length === 3) {
      const [d, m, y] = parts;
      const year = y.length === 2 ? `20${y}` : y;
      return `${year}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return "";
  })();

  const isAccountProfile =
    props.fromSettingOrLogout === "fromSettingOrLogout";

  return (
    <div className="w-full">
      {!isAccountProfile ? (
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-[#111827]">
            Team Information
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Please create a team member
          </p>
        </div>
      ) : null}

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-[#111827]">
            {isAccountProfile
              ? "Personal Information"
              : "New Team Information"}
          </h3>
          <p className="mt-1 text-sm text-[#6B7280]">
            {isAccountProfile
              ? "Manage your personal information"
              : "Manage team personal information. Profile photo is optional."}
          </p>
        </div>
        <div className="relative">
          <div className="h-20 w-20 overflow-hidden rounded-full border border-[#E5E7EB] bg-[#F3F4F6]">
            <img
              src={
                personalDetailState?.profile_image?.value
                  ? personalDetailState.profile_image.value
                  : avatarImg
              }
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <button
            type="button"
            className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-[#374151] shadow-sm"
            onClick={handleUploadClick}
            aria-label="Upload profile (optional)"
            title="Profile photo (optional)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 15.2A3.2 3.2 0 1 0 12 8.8a3.2 3.2 0 0 0 0 6.4zm8-5.2h-1.2l-.9-1.5A2 2 0 0 0 16.2 7H7.8a2 2 0 0 0-1.7 1.5L5.2 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {showOrgSelect && (
          <div className="lg:col-span-2">
            <OrganizationSelect
              multiple
              label="Organizations"
              required
              value={organizationIds}
              onChange={setOrganizationIds}
            />
            <p className="mt-1 text-xs text-[#9CA3AF]">
              Assign this user to one or more organizations. Required for CRM
              access and org switching after login.
            </p>
          </div>
        )}
        <YhInput
          label="Employee ID"
          placeholder="Enter ID"
          value={personalDetailState?.employee_id?.value || ""}
          error={personalDetailState?.employee_id?.error}
          name="employee_id"
          onChange={handleOnChangeInputField}
          onKeyPress={handleKeyPress}
        />

        <YhInput
          label="Full Name"
          placeholder="Full Name"
          value={personalDetailState?.name?.value || ""}
          error={personalDetailState?.name?.error}
          onChange={handleOnChangeInputField}
          name="name"
          onKeyPress={handleKeyPress}
        />

        <YhInput
          label="Work Email"
          placeholder="xyz@xyz.com"
          value={personalDetailState?.email?.value || ""}
          error={personalDetailState?.email?.error}
          name="email"
          onChange={handleOnChangeInputField}
          onKeyPress={handleKeyPress}
        />

        <div className="grid grid-cols-[120px_1fr] gap-2">
          <YhSelect
            label="Code"
            placeholder="Code"
            value={phoneCode}
            error={personalDetailState?.phoneCountryCode?.error}
            options={countryData.map((c) => ({
              label: `${c.label} (${c.dialCode})`,
              value: c.code,
            }))}
            onChange={(e) => {
              const selected =
                countryData.find((c) => c.code === e.target.value) || "";
              handleAutocompleteChange("phoneCountryCode", selected);
            }}
            onKeyPress={handleKeyPress as any}
          />
          <YhInput
            label="Mobile No."
            placeholder="0000-000-000"
            value={personalDetailState?.phone?.value || ""}
            error={personalDetailState?.phone?.error}
            onChange={handleOnChangeInputField}
            name="phone"
            onKeyPress={handleKeyPress}
          />
        </div>

        <div className="grid grid-cols-[120px_1fr] gap-2">
          <YhSelect
            label="Code"
            placeholder="Code"
            value={whatsappCode}
            error={personalDetailState?.whatsappCountryCode?.error}
            options={countryData.map((c) => ({
              label: `${c.label} (${c.dialCode})`,
              value: c.code,
            }))}
            onChange={(e) => {
              const selected =
                countryData.find((c) => c.code === e.target.value) || "";
              handleAutocompleteChange("whatsappCountryCode", selected);
            }}
            onKeyPress={handleKeyPress as any}
          />
          <YhInput
            label="WhatsApp Number"
            placeholder="0000-000-000"
            value={personalDetailState?.whatsapp?.value || ""}
            error={personalDetailState?.whatsapp?.error}
            onChange={handleOnChangeInputField}
            name="whatsapp"
            onKeyPress={handleKeyPress}
          />
        </div>

        <YhInput
          label="Date of Birth"
          type="date"
          value={dobForInput}
          error={personalDetailState?.dob?.error}
          name="dob"
          onChange={(e) => {
            const v = e.target.value;
            if (!v) {
              setPersonalDetailState({
                ...personalDetailState,
                dob: { value: null, error: "" },
              });
              return;
            }
            const [y, m, d] = v.split("-");
            setPersonalDetailState({
              ...personalDetailState,
              dob: { value: `${d}-${m}-${y}`, error: "" },
            });
          }}
          onKeyPress={handleKeyPress}
        />

        <YhSelect
          label="Gender"
          placeholder="Select your gender"
          value={personalDetailState?.gender?.value || ""}
          error={personalDetailState?.gender?.error}
          options={ganderOptions.map((g) => ({ label: g, value: g }))}
          onChange={(e) =>
            handleAutocompleteChange("gender", e.target.value || "")
          }
          onKeyPress={handleKeyPress as any}
        />

        <YhSelect
          label="Marital Status"
          placeholder="Select your marital status"
          value={personalDetailState?.martial_status?.value || ""}
          error={personalDetailState?.martial_status?.error}
          options={options.map((o) => ({ label: o, value: o }))}
          onChange={(e) =>
            handleAutocompleteChange("martial_status", e.target.value || "")
          }
          onKeyPress={handleKeyPress as any}
        />

        <YhInput
          label="Address (Line 1)"
          placeholder="Enter your address"
          value={personalDetailState?.address1?.value || ""}
          error={personalDetailState?.address1?.error}
          name="address1"
          onChange={handleOnChangeInputField}
          onKeyPress={handleKeyPress}
        />

        <YhInput
          label="Address (Line 2)"
          placeholder="Enter your address"
          value={personalDetailState?.address2?.value || ""}
          error={personalDetailState?.address2?.error}
          name="address2"
          onChange={handleOnChangeInputField}
          onKeyPress={handleKeyPress}
        />

        <YhSelect
          label="Country"
          placeholder="Select your country"
          value={countryId}
          error={personalDetailState?.country_id?.error}
          options={country.map((c: any) => ({
            label: c.country_name,
            value: c._id,
          }))}
          onFocus={() => onClickAutoComplet("country")}
          onChange={(e) => {
            const selected =
              country.find((c: any) => c._id === e.target.value) || null;
            if (!selected) return;
            handleAutocompleteChange("country_id", selected);
            setState([]);
            setPersonalDetailState((prev: any) => ({
              ...prev,
              country_id: {
                error: "",
                value: selected.country_name,
                id: selected._id,
              },
              state_id: { error: "", value: "", id: "" },
            }));
          }}
        />

        <YhSelect
          label="State"
          placeholder="Select your state"
          value={stateId}
          error={personalDetailState?.state_id?.error}
          options={state.map((s: any) => ({
            label: s.state_name,
            value: s._id,
          }))}
          onFocus={() => onClickAutoComplet("state")}
          onChange={(e) => {
            const selected =
              state.find((s: any) => s._id === e.target.value) || "";
            handleAutocompleteChange("state_id", selected);
          }}
        />

        <YhInput
          label="City"
          placeholder="City"
          error={personalDetailState?.city?.error}
          value={personalDetailState?.city?.value || ""}
          onChange={handleOnChangeInputField}
          name="city"
          type="text"
          onKeyPress={handleKeyPress}
        />

        <YhInput
          label="ZIP Code"
          placeholder="ZIP Code"
          value={personalDetailState?.zipcode?.value || ""}
          error={personalDetailState?.zipcode?.error}
          name="zipcode"
          onChange={handleOnChangeInputField}
          onKeyPress={handleKeyPress}
        />

        {props.hideFooterButtons ? null : (
          <div className="lg:col-span-2">
            <YhButton variant="primary" onClick={submitForm}>
              {`${props.userId ? "" : "Save & "}Continue`}
            </YhButton>
          </div>
        )}
      </div>

      {apiResponse && <CustomLoader apiResponse={false} />}
    </div>
  );
};

export default PersonalDetails;
